import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { apiInstance } from '../../axiosApi';

export const areaListGet = createAsyncThunk('product/area', async (params) => {
  return await apiInstance.get('/area', { params });
});

export const areaAdd = createAsyncThunk('product/area-add', async (payload) => {
  return await apiInstance.post('/area', payload);
});

export const areaUpdate = createAsyncThunk('product/area-update', async (payload) => {
  const { id, ...rest } = payload;
  return await apiInstance.put(`/area/${id}`, rest);
});

export const areaUpload = createAsyncThunk('product/upload-area', async (payload) => {
  return await apiInstance.post('/area/upload-area', payload);
});

export const areaDelete = createAsyncThunk('product/area-delete', async (id) => {
  return await apiInstance.delete(`/area/${id}`);
});

export const areaOptionsGet = createAsyncThunk('product/area-options', async () => {
  return await apiInstance.get('/area');
});

export const beatListGet = createAsyncThunk('product/beat', async (params) => {
  return await apiInstance.get('/beat', { params });
});

export const beatAdd = createAsyncThunk('product/beat-add', async (payload) => {
  return await apiInstance.post('/beat', payload);
});

export const beatUpdate = createAsyncThunk('product/beat-update', async (payload) => {
  const { id, ...rest } = payload;
  return await apiInstance.put(`/beat/${id}`, rest);
});

export const beatDelete = createAsyncThunk('product/beat-delete', async (id) => {
  return await apiInstance.delete(`/beat/${id}`);
});

export const beatUpload = createAsyncThunk('product/upload-beat', async (payload) => {
  return await apiInstance.post('/beat/upload-beat', payload);
});

export const beatOptionsGet = createAsyncThunk('product/beat-options', async () => {
  return await apiInstance.get('/beat');
});

export const beatOptionsGetById = createAsyncThunk(
  'product/beat-options-by-area',
  async (params) => {
    return await apiInstance.get('/beat', { params });
  }
);

export const outletListGet = createAsyncThunk('product/outlet', async (params) => {
  return await apiInstance.get('/outlet', { params });
});

export const outletOptionsGetById = createAsyncThunk('product/outlet-by-beat', async (params) => {
  return await apiInstance.get('/outlet', { params });
});

export const outletAdd = createAsyncThunk('product/outlet-add', async (payload) => {
  return await apiInstance.post('/outlet', payload);
});

export const outletUpdate = createAsyncThunk('product/outlet-update', async (payload) => {
  const { id, ...rest } = payload;
  return await apiInstance.put(`/outlet/${id}`, rest);
});

export const outletUpload = createAsyncThunk('product/upload-outlet', async (payload) => {
  return await apiInstance.post('/outlet/upload-outlet', payload);
});

export const outletDelete = createAsyncThunk('product/outlet-delete', async (id) => {
  return await apiInstance.delete(`/outlet/${id}`);
});

export const productListGet = createAsyncThunk('product/product', async (params) => {
  return await apiInstance.get('/product', { params });
});

export const crmProductListGet = createAsyncThunk('product/crm-product', async (params) => {
  return await apiInstance.get('/product', { params });
});

export const productAdd = createAsyncThunk('product/product-add', async (payload) => {
  return await apiInstance.post('/product', payload);
});

export const productUpdate = createAsyncThunk('product/product-update', async (payload) => {
  const { id, ...rest } = payload;
  return await apiInstance.put(`/product/${id}`, rest);
});

export const productUpload = createAsyncThunk('product/upload-product', async (payload) => {
  return await apiInstance.post('/product/upload-product', payload);
});

export const productDelete = createAsyncThunk('product/product-delete', async (id) => {
  return await apiInstance.delete(`/product/${id}`);
});

export const productSave = createAsyncThunk('product/save_product', async (payload) => {
  return await apiInstance.post('/product/save_product', payload);
});

export const productReportGet = createAsyncThunk('report/product_report', async (params) => {
  return await apiInstance.get('/product/product_report', { params });
});

export const productReportDetailsGet = createAsyncThunk(
  'product/product_report_details',
  async (id) => {
    return await apiInstance.get(`/product/product_report_details/${id}`);
  }
);

const initialState = {
  areaList: null,
  beatList: null,
  outletList: null,
  productList: null
};

const productSlice = createSlice({
  name: 'product',
  initialState,
  reducers: {
    clearProductSlice: () => initialState
  },
  extraReducers: (builder) => {
    builder.addCase(areaListGet.fulfilled, (state, action) => {
      state.areaList = action?.payload;
    });
    builder.addCase(beatListGet.fulfilled, (state, action) => {
      state.beatList = action?.payload;
    });
    builder.addCase(outletListGet.fulfilled, (state, action) => {
      state.outletList = action?.payload;
    });
    builder.addCase(productListGet.fulfilled, (state, action) => {
      state.productList = action?.payload;
    });
  }
});

export default productSlice.reducer;
export const { clearProductSlice } = productSlice.actions;

export const selectAreaList = (state) => state.product.areaList;
export const useAreaList = () => {
  const areaList = useSelector(selectAreaList);
  return useMemo(() => areaList, [areaList]);
};

export const selectBeatList = (state) => state.product.beatList;
export const useBeatList = () => {
  const beatList = useSelector(selectBeatList);
  return useMemo(() => beatList, [beatList]);
};

export const selectOutletList = (state) => state.product.outletList;
export const useOutletList = () => {
  const outletList = useSelector(selectOutletList);
  return useMemo(() => outletList, [outletList]);
};

export const selectProductList = (state) => state.product.productList;
export const useProductList = () => {
  const productList = useSelector(selectProductList);
  return useMemo(() => productList, [productList]);
};
